import React, { useCallback, useState, useEffect } from "react";

import { useNavigate, Link } from "react-router-dom";

import { Label, Pie, PieChart } from "recharts";
import {
  Plus,
  Pencil,
  UserCog,
  Merge,
  MoreHorizontal,
  Trash2,
  ScrollText,
  Loader2,
} from "lucide-react";

import firestoreService from "../../services/firestoreService";
import functionsService from "../../services/functionsService";
import analyticsService from "../../services/analyticsService";

import { auth } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { where } from "firebase/firestore";

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../components/ui/chart";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../components/ui/tooltip";

import { useToast } from "../../components/hooks/use-toast";
import { CommonDialog } from "../../components/CommonDialog";

import ProjectJoinModal from "./ProjectJoin";

const styles = `
  .project-name, .project-description {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    padding-bottom: 2px;
  }

  .project-name {
    font-size: 16px;
  }

  .project-description {
    font-size: 14px;
  }
`;

function Projects() {
  const [projects, setProjects] = useState([]);

  const [checkingUserRole, setCheckingUserRole] = useState(true);
  const [canCreateProject, setCanCreateProject] = useState(false);

  const [joinModalOpen, setJoinModalOpen] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const chartConfig = {
    hazards: {
      label: "Hazards",
    },
    none: {
      label: "No hazards",
      color: "#000000",
    },
    open: {
      label: "Open",
      color: "var(--risk-status-open)",
    },
    managed: {
      label: "Managed",
      color: "var(--risk-status-managed)",
    },
    resolved: {
      label: "Resolved",
      color: "var(--risk-status-resolved)",
    },
    forTransfer: {
      label: "For Transfer",
      color: "var(--risk-status-forTransfer)",
    },
    transferred: {
      label: "Transferred",
      color: "var(--risk-status-transferred)",
    },
    closed: {
      label: "Closed",
      color: "var(--risk-status-closed)",
    },
    cancelled: {
      label: "Cancelled",
      color: "var(--risk-status-cancelled)",
    },
  };

  const getTotalHazards = (chartData) => {
    return chartData.reduce((acc, curr) => acc + curr.hazards, 0);
  };

  const getChartData = (project) => {
    return [
      {
        riskStatus: "Open",
        hazards: project.hazardStatusCount.open,
        fill: "var(--risk-status-open)",
      },
      {
        riskStatus: "Managed",
        hazards: project.hazardStatusCount.managed,
        fill: "var(--risk-status-managed)",
      },
      {
        riskStatus: "Resolved",
        hazards: project.hazardStatusCount.resolved,
        fill: "var(--risk-status-resolved)",
      },
      {
        riskStatus: "For Transfer",
        hazards: project.hazardStatusCount.forTransfer,
        fill: "var(--risk-status-forTransfer)",
      },
      {
        riskStatus: "Transferred",
        hazards: project.hazardStatusCount.transferred,
        fill: "var(--risk-status-transferred)",
      },
      {
        riskStatus: "Closed",
        hazards: project.hazardStatusCount.closed,
        fill: "var(--risk-status-closed)",
      },
      {
        riskStatus: "Cancelled",
        hazards: project.hazardStatusCount.cancelled,
        fill: "var(--risk-status-cancelled)",
      },
    ];
  };

  const navigate = useNavigate();
  const { toast } = useToast();

  // Fetch projects from Firestore when the component mounts
  const fetchProjects = useCallback(
    async (userId) => {
      try {
        // Run all three queries to retrieve any projects where current user is Owner, Editor or Viewer
        const [ownerProjects, editorProjects, viewerProjects] =
          await Promise.all([
            firestoreService.getWhere(
              "projects",
              where("roles.owners", "array-contains", userId)
            ),
            firestoreService.getWhere(
              "projects",
              where("roles.editors", "array-contains", userId)
            ),
            firestoreService.getWhere(
              "projects",
              where("roles.viewers", "array-contains", userId)
            ),
          ]);

        // Combine results from all three snapshots into a single array
        const projectsList = [
          ...ownerProjects,
          ...editorProjects,
          ...viewerProjects,
        ];

        setProjects(projectsList);
      } catch (error) {
        console.error("Error fetching projects.", error);

        toast({
          variant: "destructive",
          title: "Error fetching projects.",
          description: error.message,
        });
      }
    },
    [toast]
  );

  useEffect(() => {
    const checkUserRole = async (user) => {
      setCheckingUserRole(true);

      try {
        // Get the ID token and decode the custom claims
        const idTokenResult = await user.getIdTokenResult();

        // Check if the user has the 'admin' or 'manager' roles
        if (
          idTokenResult.claims.role === "admin" ||
          idTokenResult.claims.role === "manager"
        ) {
          setCanCreateProject(true);
        }
      } catch (error) {
        console.error("Error checking role:", error);
      } finally {
        setCheckingUserRole(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Force token refresh to retrieve the new custom claims if any
        await user.getIdToken(true);

        checkUserRole(user);
        fetchProjects(user.uid);
      } else {
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, [fetchProjects, navigate, toast]);

  const deleteProject = async (project) => {
    try {
      // Recursive deletion of project and all its subcollections
      const { success, error } = await functionsService.deleteProject(
        project.id
      );

      if (!success) {
        console.error("Error deleting project.", error);
        
        // Track error in analytics
        analyticsService.trackError('project_deletion_error', error.message, 'deleteProject');

        toast({
          variant: "destructive",
          title: "Error deleting project.",
          description: error.message,
        });
      } else {
        // Track successful project deletion
        analyticsService.trackFeatureUsage('delete_project', project.id);
      }

      setProjects((x) => x.filter((y) => y.id !== project.id));
    } catch (error) {
      console.error("Error deleting project.", error);
      
      // Track error in analytics
      analyticsService.trackError('project_deletion_error', error.message, 'deleteProject');

      toast({
        variant: "destructive",
        title: "Error deleting project.",
        description: error.message,
      });
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const navigateToHazards = (project) => {
    // Go to properties if project metadata not set yet
    if (!project.projectMetadata) {
      navigate(`/projects/${project.id}/properties`);
      return;
    }

    // Go to context if project context not set yet
    if (!project.context) {
      navigate(`/projects/${project.id}/properties`);
      return;
    }

    navigate(`/projects/${project.id}/hazards`);
  };

  return (
    <>
      <style>{styles}</style>

      <Card className="h-full">
        <CardHeader>
          <CardTitle>Projects</CardTitle>
        </CardHeader>

        <CardContent>
          <Link to={"/projects/add"}>
            <Button
              className="min-w-[135px]"
              disabled={checkingUserRole || !canCreateProject}
            >
              {checkingUserRole ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                </>
              ) : (
                <>
                  <Plus size={16} className="mr-2" />
                  <span>Add project</span>
                </>
              )}
            </Button>
          </Link>

          <Button
            className="min-w-[135px] ml-4"
            variant="outline"
            onClick={() => setJoinModalOpen(true)}
          >
            <Merge size={16} className="mr-2" />
            Join project
          </Button>

          <br />
          <br />

          <div className="grid flex-1 scroll-mt-20 items-start gap-10 md:grid-cols-2 md:gap-6 lg:grid-cols-3 xl:gap-10">
            {projects.map((project) => {
              const isOwner = project.roles.owners.includes(
                auth.currentUser.uid
              );

              let chartData = getChartData(project);
              const totalHazards = getTotalHazards(chartData);

              // If no hazards, display empty 'No hazards' chart
              if (totalHazards === 0) {
                chartData = [
                  {
                    riskStatus: "No Hazards",
                    hazards: 1,
                    fill: "var(--risk-status-none)",
                  },
                ];
              }

              return (
                <div key={project.id}>
                  <ProjectJoinModal
                    modalOpen={joinModalOpen}
                    setModalOpen={setJoinModalOpen}
                  />

                  <CommonDialog
                    open={deleteDialogOpen}
                    setOpen={setDeleteDialogOpen}
                    title={`Delete project "${project?.name}"?`}
                    description={
                      "This cannot be undone. All associated hazards with this project will also be deleted. Proceed with deletion?"
                    }
                    onConfirm={() => deleteProject(project)}
                  />

                  <Card className="flex flex-col" key={project.id}>
                    <CardHeader className="items-center pb-0">
                      <CardTitle className="project-name text-center">
                        {project.name}
                      </CardTitle>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="project-description text-center leading-none text-muted-foreground">
                            {project.description}
                          </div>
                        </TooltipTrigger>

                        <TooltipContent>
                          <p>{project.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    </CardHeader>

                    <CardContent className="flex-1 pb-0">
                      <ChartContainer
                        config={chartConfig}
                        className="mx-auto aspect-square max-h-[250px]"
                      >
                        <PieChart>
                          {totalHazards > 0 && (
                            <ChartTooltip
                              cursor={false}
                              content={<ChartTooltipContent hideLabel />}
                            />
                          )}

                          <Pie
                            data={chartData}
                            dataKey="hazards"
                            nameKey="riskStatus"
                            innerRadius={60}
                            strokeWidth={5}
                          >
                            <Label
                              content={({ viewBox }) => {
                                if (
                                  viewBox &&
                                  "cx" in viewBox &&
                                  "cy" in viewBox
                                ) {
                                  return (
                                    <text
                                      x={viewBox.cx}
                                      y={viewBox.cy}
                                      textAnchor="middle"
                                      dominantBaseline="middle"
                                    >
                                      <tspan
                                        x={viewBox.cx}
                                        y={viewBox.cy}
                                        className="fill-foreground text-3xl font-bold"
                                      >
                                        {totalHazards.toLocaleString()}
                                      </tspan>

                                      <tspan
                                        x={viewBox.cx}
                                        y={(viewBox.cy || 0) + 24}
                                        className="fill-muted-foreground"
                                      >
                                        Hazards
                                      </tspan>
                                    </text>
                                  );
                                }
                              }}
                            />
                          </Pie>
                        </PieChart>
                      </ChartContainer>
                    </CardContent>

                    <CardFooter className="flex-col gap-2 text-sm">
                      <div className="items-center">
                        <Button
                          size="sm"
                          onClick={() => navigateToHazards(project)}
                        >
                          <ScrollText className="mr-2" size={16} />
                          Hazards
                        </Button>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            {isOwner && (
                              <Button
                                variant="outline"
                                size="icon"
                                className="mx-2 h-[36px] w-[36px]"
                              >
                                <MoreHorizontal size={16} />
                              </Button>
                            )}
                          </DropdownMenuTrigger>

                          <DropdownMenuContent
                            className="w-48 rounded-lg"
                            side="right"
                            align="start"
                          >
                            <Link to={`/projects/${project.id}/edit`}>
                              <DropdownMenuItem>
                                <Pencil
                                  className="mr-2 text-muted-foreground"
                                  size={16}
                                />
                                <span>Edit Project</span>
                              </DropdownMenuItem>
                            </Link>

                            <Link
                              to={`/projects/${project.id}/access`}
                              state={{ project }}
                            >
                              <DropdownMenuItem>
                                <UserCog
                                  className="mr-2 text-muted-foreground"
                                  size={16}
                                />
                                <span>Edit User Access</span>
                              </DropdownMenuItem>
                            </Link>

                            <DropdownMenuSeparator />

                            <DropdownMenuItem
                              onClick={() => {
                                setTimeout(() => {
                                  setDeleteDialogOpen(true);
                                }, 200);
                              }}
                            >
                              <Trash2
                                className="mr-2 text-muted-foreground"
                                size={16}
                              />
                              <span>Delete Project</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardFooter>
                  </Card>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </>
  );
}

export default Projects;
